import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FrostOverlayMenuService } from './frost-overlay-menu.service';
import { FrostMenuItem, MenuSeparator, isMenuItem } from './frost-overlay-menu.model';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FrostMenuManagerService } from 'app/shared/services/frost-menu-manager.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'frost-overlay-menu',
    templateUrl: './frost-overlay-menu.component.html',
    styleUrls: ['./frost-overlay-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('menuAnimation', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateY(8px)',
                }),
                animate('120ms ease-out', style({
                    opacity: 1,
                    transform: 'translateY(0)',
                })),
            ]),
            transition(':leave', [
                animate('80ms ease-in', style({
                    opacity: 0,
                    transform: 'translateY(8px)',
                })),
            ]),
        ]),
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('120ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('80ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0 })),
            ]),
        ]),
        trigger('rotateIcon', [
            transition(':enter', [
                style({ transform: 'rotate(-90deg)' }),
                animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'rotate(0deg)' })),
            ]),
            transition(':leave', [
                animate('100ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'rotate(-90deg)' })),
            ]),
        ]),
        trigger('pulseAnimation', [
            transition(':enter', [
                animate('800ms', keyframes([
                    style({ transform: 'scale(1)', offset: 0 }),
                    style({ transform: 'scale(1.05)', offset: 0.5 }),
                    style({ transform: 'scale(1)', offset: 1 })
                ]))
            ])
        ]),
        trigger('hoverAnimation', [
            transition('* => hovered', [
                animate('200ms', style({
                    backgroundColor: 'rgba(243, 244, 246, 0.9)'
                }))
            ]),
            transition('hovered => *', [
                animate('200ms', style({ backgroundColor: 'transparent' }))
            ])
        ])
    ],
    standalone: false
})
export class FrostOverlayMenuComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    private _clickOutsideListener: any;

    @ViewChild('menuContainer') menuContainer: ElementRef;
    @ViewChild('triggerButton') triggerButton: ElementRef;

    @ContentChild('menuHeader') menuHeaderTemplate: TemplateRef<any>;
    @ContentChild('menuFooter') menuFooterTemplate: TemplateRef<any>;
    @ContentChild('customTrigger') customTriggerTemplate: TemplateRef<any>;
    @ContentChild('menuItemTemplate') menuItemTemplate: TemplateRef<any>;
    @ContentChild('menuContent') menuContentTemplate: TemplateRef<any>;

    @Input() menuTitle: string = '';
    @Input() menuDescription: string = '';
    @Input() menuItems: (FrostMenuItem | MenuSeparator)[] = [];
    @Input() position: string = 'top'; // Her zaman 'top' olarak kullanılır
    @Input() triggerIcon: IconProp = ['fal', 'bars'];
    @Input() triggerIconColor: string = '';
    @Input() triggerButtonClass: string = '';
    @Input() showArrow: boolean = true;
    @Input() maxHeight: string = '80vh';
    @Input() minWidth: string = '286px';
    @Input() backdropBlur: boolean = true;
    @Input() useRoundButtons: boolean = false;
    @Input() closeOnSelect: boolean = true;
    @Input() closeOnClickOutside: boolean = true;
    @Input() useHoverAnimations: boolean = true;
    @Input() buttonMode: boolean = false;
    @Input() routeLink: string = '';
    @Input() darkTheme: boolean = false;
    @Input() emptyStateText: string = '';

    @Output() menuOpened: EventEmitter<void> = new EventEmitter<void>();
    @Output() menuClosed: EventEmitter<void> = new EventEmitter<void>();
    @Output() menuItemSelected: EventEmitter<FrostMenuItem> = new EventEmitter<FrostMenuItem>();
    @Output() buttonClick = new EventEmitter<MouseEvent>();

    isOpen: boolean = false;
    activeSubmenuId: string | null = null;
    hoverState: { [key: string]: boolean } = {};

    // Menü için benzersiz ID
    private _menuId: string;

    // Mouse pozisyonu bilgisi için değişkenler
    private _clickPosition: { x: number; y: number } = { x: 0, y: 0 };

    constructor(
        private _elementRef: ElementRef,
        private _changeDetectorRef: ChangeDetectorRef,
        private _frostOverlayMenuService: FrostOverlayMenuService,
        private _router: Router,
        private _frostMenuManager: FrostMenuManagerService,
        private _translateService: TranslateService
    ) {
        // Varsayılan boş durum metni için çeviriyi al
        this._translateService.get('SystemAdmin.Components.FrostOverlayMenu.EmptyState').subscribe((res: string) => {
            this.emptyStateText = res;
        });
    }

    ngOnInit(): void {
        // Register click outside listener
        if (this.closeOnClickOutside) {
            this._setupClickOutsideListener();
        }

        // Close when another menu opens
        this._frostOverlayMenuService.onMenuOpened
            .pipe(
                filter(component => component !== this),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.close();
            });

        // Menü başlangıçta açıksa servise bildir
        if (this.isOpen) {
            this._frostMenuManager.registerOpenMenu(this._getMenuId());
        }
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Remove click outside listener
        this._removeClickOutsideListener();

        // Bileşen yok edildiğinde menü kapanmış olur
        if (this.isOpen) {
            this._frostMenuManager.registerClosedMenu(this._getMenuId());
        }
    }

    /**
     * Toggle the menu
     */
    toggle(event: MouseEvent): void {
        if (this.buttonMode) {
            if (this.routeLink) {
                // Eğer routeLink tanımlandıysa, direkt o route'a yönlendir
                this._router.navigate([this.routeLink]);
            } else {
                // Routelink yoksa, buttonClick eventini tetikle (geriye uyumluluk için)
                this.buttonClick.emit(event);
            }
            return;
        }

        // Mouse tıklama pozisyonunu kaydet
        if (event) {
            this._clickPosition = { x: event.clientX, y: event.clientY };
        }

        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.activeSubmenuId = null;
            this._frostOverlayMenuService.onMenuOpened.next(this);

            setTimeout(() => {
                this._checkMenuBoundaries();
                this._positionMenuAtClickPoint();
            });

            // Menü açıldı - merkezi servise bildir
            this._frostMenuManager.registerOpenMenu(this._getMenuId());
            this.menuOpened.emit();
        } else {
            this.activeSubmenuId = null;
            this.menuClosed.emit();
            this._changeDetectorRef.markForCheck();

            // Menü kapandı - merkezi servise bildir
            this._frostMenuManager.registerClosedMenu(this._getMenuId());
        }
    }

    /**
     * Open the menu
     */
    open(): void {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;
        this.activeSubmenuId = null;
        this._frostOverlayMenuService.onMenuOpened.next(this);

        setTimeout(() => {
            this._checkMenuBoundaries();
            this._positionMenuAtClickPoint();
        });

        // Merkezi servise bildir
        this._frostMenuManager.registerOpenMenu(this._getMenuId());

        // Event'i tetikle
        this.menuOpened.emit();

        // Değişiklikleri işle
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Close the menu
     */
    close(): void {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;
        this.activeSubmenuId = null;
        this.menuClosed.emit();
        this._changeDetectorRef.markForCheck();

        // Merkezi servise bildir
        this._frostMenuManager.registerClosedMenu(this._getMenuId());
    }

    /**
     * Toggle submenu
     */
    toggleSubmenu(menuItem: FrostMenuItem, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (this.activeSubmenuId === menuItem.id) {
            this.activeSubmenuId = null;
        } else {
            this.activeSubmenuId = menuItem.id;
        }

        this._changeDetectorRef.markForCheck();
    }

    /**
     * Handle menu item click
     */
    onMenuItemClick(menuItem: FrostMenuItem | MenuSeparator, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }

        // Skip if it's a separator
        if (!isMenuItem(menuItem)) {
            return;
        }

        // Skip action if item has children and is meant to toggle submenu
        if (menuItem.children && menuItem.children.length > 0 && !menuItem.executeOnClick) {
            this.toggleSubmenu(menuItem, event);
            return;
        }

        // Skip action if item is disabled
        if (menuItem.disabled) {
            return;
        }

        // Execute action if available
        if (menuItem.action && typeof menuItem.action === 'function') {
            menuItem.action(menuItem);
        }

        // Emit selected event
        this.menuItemSelected.emit(menuItem);

        // Close menu if configured to do so
        if (this.closeOnSelect && !menuItem.keepMenuOpen) {
            this.close();
        }
    }

    /**
     * Check if item is a separator
     */
    isSeparator(item: FrostMenuItem | MenuSeparator): boolean {
        return 'isSeparator' in item && item.isSeparator === true;
    }

    /**
     * Check if item is a menu item (not a separator)
     */
    isMenuItem(item: FrostMenuItem | MenuSeparator): boolean {
        return !this.isSeparator(item);
    }

    /**
     * Get menu item icon safely
     */
    getItemIcon(item: FrostMenuItem | MenuSeparator): IconProp | undefined {
        return this.isMenuItem(item) ? (item as FrostMenuItem).icon : undefined;
    }

    /**
     * Get menu item icon color safely
     */
    getItemIconColor(item: FrostMenuItem | MenuSeparator): string {
        return this.isMenuItem(item) && (item as FrostMenuItem).iconColor ? (item as FrostMenuItem).iconColor : '';
    }

    /**
     * Get the label color of a menu item
     */
    getItemLabelColor(item: FrostMenuItem | MenuSeparator): string {
        return this.isMenuItem(item) && (item as FrostMenuItem).labelColor ? (item as FrostMenuItem).labelColor : '';
    }

    /**
     * Get menu item badge safely
     */
    getItemBadge(item: FrostMenuItem | MenuSeparator): string | number | undefined {
        return this.isMenuItem(item) ? (item as FrostMenuItem).badge : undefined;
    }

    /**
     * Get menu item badge color safely
     */
    getItemBadgeColor(item: FrostMenuItem | MenuSeparator): string {
        return this.isMenuItem(item) ? (item as FrostMenuItem).badgeColor || 'bg-blue-500' : 'bg-blue-500';
    }

    /**
     * Check if menu item has children safely
     */
    hasChildren(item: FrostMenuItem | MenuSeparator): boolean {
        return this.isMenuItem(item) && !!(item as FrostMenuItem).children && (item as FrostMenuItem).children!.length > 0;
    }

    /**
     * Get menu item id safely
     */
    getItemId(item: FrostMenuItem | MenuSeparator): string | undefined {
        return this.isMenuItem(item) ? (item as FrostMenuItem).id : undefined;
    }

    /**
     * Check if menu item is active
     */
    isItemActive(item: FrostMenuItem | MenuSeparator): boolean {
        return this.isMenuItem(item) && this.activeSubmenuId === (item as FrostMenuItem).id;
    }

    /**
     * Check if menu item has description
     */
    hasDescription(item: FrostMenuItem | MenuSeparator): boolean {
        return this.isMenuItem(item) && !!(item as FrostMenuItem).description;
    }

    /**
     * Get menu item description safely
     */
    getItemDescription(item: FrostMenuItem | MenuSeparator): string | undefined {
        return this.isMenuItem(item) ? (item as FrostMenuItem).description : undefined;
    }

    /**
     * Check if menu item is disabled
     */
    isItemDisabled(item: FrostMenuItem | MenuSeparator): boolean {
        return this.isMenuItem(item) && !!(item as FrostMenuItem).disabled;
    }

    /**
     * Get menu item children safely
     */
    getItemChildren(item: FrostMenuItem | MenuSeparator): (FrostMenuItem | MenuSeparator)[] | undefined {
        return this.isMenuItem(item) ? (item as FrostMenuItem).children : undefined;
    }

    /**
     * Set up click outside listener
     */
    private _setupClickOutsideListener(): void {
        this._removeClickOutsideListener();

        this._clickOutsideListener = fromEvent(document, 'click')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event: MouseEvent) => {
                // Return if the menu is not open
                if (!this.isOpen) {
                    return;
                }

                // Get the clicked element
                const target = event.target as HTMLElement;

                // Return if the clicked element is within the menu
                if (this._elementRef.nativeElement.contains(target)) {
                    return;
                }

                // Close the menu
                this.close();
            });
    }

    /**
     * Remove click outside listener
     */
    private _removeClickOutsideListener(): void {
        if (this._clickOutsideListener) {
            this._clickOutsideListener.unsubscribe();
            this._clickOutsideListener = null;
        }
    }

    /**
     * Ensures the menu stays within viewport boundaries
     * Uses simple logic to keep menu visible
     */
    private _checkMenuBoundaries(): void {
        if (!this.menuContainer || !this.isOpen) {
            return;
        }

        const menuElement = this.menuContainer.nativeElement;
        const menuRect = menuElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Reset any previously added boundary classes
        menuElement.classList.remove('boundary-right', 'boundary-left', 'boundary-top', 'boundary-bottom');

        // Simple boundary checks
        if (menuRect.right > viewportWidth) {
            // Menü sağa taşıyorsa, pozisyonu değiştir
            menuElement.classList.add('boundary-right');
        }

        if (menuRect.bottom > viewportHeight) {
            // Menü aşağı taşıyorsa, pozisyonu değiştir
            menuElement.classList.add('boundary-bottom');
        }

        if (menuRect.left < 0) {
            // Menü sola taşıyorsa, pozisyonu değiştir
            menuElement.classList.add('boundary-left');
        }

        if (menuRect.top < 0) {
            // Menü yukarı taşıyorsa, pozisyonu değiştir
            menuElement.classList.add('boundary-top');
        }

        // Force refresh to apply the changes
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Set hover state for a menu item
     */
    setHoverState(menuItem: FrostMenuItem | MenuSeparator, isHovered: boolean): void {
        if (!this.useHoverAnimations || !isMenuItem(menuItem)) {
            return;
        }

        // Store hover state for animation (used in light theme)
        this.hoverState[menuItem.id] = isHovered;
        
        // For dark theme, we'll rely on CSS hover effects instead of animations
        // to avoid the white flash effect
        
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Get hover state for menu item (for template animation)
     */
    getHoverState(menuItem: FrostMenuItem | MenuSeparator): string {
        if (!this.useHoverAnimations || !isMenuItem(menuItem) || this.darkTheme) {
            return 'idle';
        }

        return this.hoverState[menuItem.id] ? 'hovered' : 'idle';
    }

    /**
     * Menü için benzersiz bir ID oluştur veya var olanı döndür
     */
    private _getMenuId(): string {
        if (!this._menuId) {
            // Menü başlığı, ikon veya rastgele bir değerden ID oluştur
            const iconStr = Array.isArray(this.triggerIcon) ? this.triggerIcon.join('-') : 'menu';
            const titleStr = this.menuTitle ? this.menuTitle.toLowerCase().replace(/\s+/g, '-') : '';
            const random = Math.floor(Math.random() * 10000);

            this._menuId = `frost-menu-${titleStr || iconStr}-${random}`;
        }

        return this._menuId;
    }

    /**
     * Menüyü tıklama noktasına göre konumlandır
     */
    private _positionMenuAtClickPoint(): void {
        if (!this.menuContainer) {
            return;
        }

        const menuElement = this.menuContainer.nativeElement;
        if (!menuElement) {
            return;
        }

        // Basit animasyon için stili direkt ayarla
        menuElement.style.transformOrigin = 'top center';
        menuElement.style.transform = 'none';
        menuElement.style.transition = 'none';
    }

    getHoverColor(): string {
        return this.darkTheme ? 'rgba(55, 65, 81, 0.7)' : 'rgba(243, 244, 246, 0.9)';
    }
}